import React, { useCallback, useEffect, useState } from "react";

import { addAdminFile, getSoilAICropHeatmap, getSoilAICropModelSummary, getSoilAICropPredictions } from "services";

import { addShapeProcessor, checkValidShapefileSize, checkValidShapefileType, normalise, processGeometries, useCancelAsyncOnUnmount } from "utils";

import {
  AdminHead,
  AdminSection,
  AdminTable,
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Form,
  InputNumber,
  MainPage,
  MapInteractive,
  Option,
  Row,
  Select,
  Spin,
  TagNormal,
  Upload,
  Message as message,
} from "components";

export const CropDetection = () => {
  const [summary, setSummary] = useState(false);
  const [year, setYear] = useState(2020);
  const [endYear, setEndyear] = useState(2022);
  const [totalYears, setTotalYears] = useState(1);
  const [easyExport, setEasyExport] = useState(false);
  const [ensemble, setEnsemble] = useState(false);
  const [groups, setGroups] = useState(false);
  const [startItem, setStartItem] = useState(0);
  const [geometries, setGeometries] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [details, setDetails] = useState(false);
  const [predictionDetails, setPredictionDetails] = useState(false);
  const [heatmap, setHeatmap] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showPredictionDetailsDrawer, setShowPredictionDetailsDrawer] = useState(false);
  const [location, setLocation] = useState({
    lat: 52.5224832,
    lng: 13.3906564,
  });
  const signal = useCancelAsyncOnUnmount()();

  const years = Array(10)
    .fill()
    .map((_, idx) => 2014 + idx);

  const loadSummary = () => {
    getSoilAICropModelSummary({
      signal,
    }).then((res) => {
      setSummary(res);
    });
  };

  const loadHeatmap = (item) => {
    getSoilAICropHeatmap({
      field: item.field,
      date: {
        startYear: item.data.crops.ndvi[0].year,
        startMonth: item.data.crops.ndvi[0].month,
        endYear: item.data.crops.ndvi[item.data.crops.ndvi.length - 1].year,
        endMonth: item.data.crops.ndvi[item.data.crops.ndvi.length - 1].month,
      },
      signal,
    }).then((res) => {
      if (res && res.length) {
        setHeatmap(res);

        setLocation({
          lat: res[0].geometry[0].lat,
          lng: res[0].geometry[0].lng,
        });
      }
    });
  };

  const openHeatmap = (item) => {
    setLocation(false);
    setHeatmap(false);
    setDetails(false);

    const prediction = predictions[item.id];

    setDetails(prediction);
    setShowDetailsDrawer(true);

    loadHeatmap({ data: prediction.data, field: geometries[item.id] });
  };

  const openPredictions = (item) => {
    const predictionList = Object.entries(predictions[item.field].data.ensemble.predictions).map(([crop, value]) => ({
      crop,
      value: `${(value * 100).toFixed(2)}%`,
    }));

    setPredictionDetails(predictionList);
    setShowPredictionDetailsDrawer(true);
  };

  const getPredictions = useCallback(
    (geometries, i, currentYear) => {
      getSoilAICropPredictions({
        field: geometries[i],
        year: currentYear,
        signal,
      })
        .then((res) => {
          const newPredictions = predictions;
          newPredictions[predictions.length] = res;
          setPredictions([...newPredictions, ...[false]]);

          // get next year for this geometry
          if (currentYear < year + totalYears) {
            getPredictions(geometries, i, currentYear + 1);
          } else if (geometries.length > i + 1) {
            // otherwise get next geometry
            getPredictions(geometries, i + 1, currentYear - totalYears);
          }
        })
        .catch((_err) => {
          getPredictions(geometries, i, currentYear);
          message({
            content: `There was a problem collecting data for field ${i}. Trying again...`,
            icon: "gremlin",
          });
        });
    },
    [signal, predictions, totalYears, year]
  );

  const onError = (error) => {
    message({
      content: `File processing failed with the error ${error}.`,
      icon: "gremlin",
    });
  };

  const processShapes = async (data) => {
    const processedGeometries = await processGeometries(data, onError);
    setGeometries(processedGeometries);
    setLocation({
      lat: processedGeometries[0].geometry[0].lat,
      lng: processedGeometries[0].geometry[0].lng,
    });
  };

  const processShapeFile = (file) => {
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();

    reader.onabort = () =>
      message({
        content: "File reading was aborted unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onerror = () =>
      message({
        content: "File reading was failed unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onloadend = async () => {
      const binaryStr = reader.result;

      if (fileType === "zip") {
        processShapes(binaryStr);
      } else {
        message({
          content: "You must upload a valid zip file with shp, prj and dbf files included.",
          icon: "gremlin",
        });
      }
    };
    if (fileType === "zip") {
      reader.readAsArrayBuffer(file);
    } else {
      message({
        content: "You must upload a valid zip file with shp, prj and dbf files included.",
        icon: "gremlin",
      });
    }
  };

  const handleUpload = (file) => {
    addAdminFile({
      file: file,
      location: "shapefiles",
    }).then(() => {
      processShapeFile(file);
      message({
        content: "Your file was accepted by the gremlins.",
        icon: "gremlin",
      });
    });
  };

  const fileUpload = async (file) => {
    const isValidType = await checkValidShapefileType(file);
    const isValidSize = await checkValidShapefileSize(file);

    if (!isValidType) {
      message({
        content: "Gremlins only eat zip files.",
        icon: "gremlin",
      });
    } else if (!isValidSize) {
      message({
        content: "Your file was too large for the gremlins.",
        icon: "gremlin",
      });
    } else {
      handleUpload(file);
    }
  };

  // first normalise current ndvi score against the min and max
  // so the highest value will have opacity = 1, and the lowest = 0
  // then multiply by 0.8 so it's not too overbearing
  const getFillOpacity = (geometry) => normalise(geometry.ndvi, Math.min(...heatmap.map((geo) => geo.ndvi)), Math.max(...heatmap.map((geo) => geo.ndvi))) * 0.8;

  useEffect(() => {
    setTotalYears(endYear - year);
  }, [year, endYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (geometries.length > 0) {
      setPredictions([false]);
      getPredictions(geometries, parseInt(startItem), year);
      loadSummary();
    }
  }, [geometries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    addShapeProcessor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage pageName={"admin"}>
      <AdminHead
        headline={
          <>
            SoilAI crop detection <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description={
          <>
            Upload a shapefile to collect data and predict crops for the past 3 years. The output of the prediction will be shown below.{" "}
            <b>It may take a long time for large qty of fields - only 1 field is processed at a time.</b>
            <br />
            <br />
            {summary
              ? `The current accuracy of the crop prediction model is ${summary.accuracyTop1}% for the top prediction, and ${summary.accuracyTop3}% for the top 3 predictions. Models are retrained daily for improving accuracy. Results are presented as Crop (probability).`
              : ""}
          </>
        }
        extra={
          predictions.length < geometries.length ? (
            <>
              <Row>
                <Col sm={24}>:( sorry - you have to refresh to use a new file/year</Col>
              </Row>
            </>
          ) : (
            <>
              <Row>
                <Col sm={8}>
                  <Form name="year">
                    <Select name="year" label="Starting year" onChange={(v) => setYear(v)} initialValue={year} style={{ textAlign: "left" }}>
                      {years.map((year, i) => (
                        <Option key={i} value={year}>
                          {year}
                        </Option>
                      ))}
                    </Select>
                  </Form>
                </Col>
                <Col sm={8}>
                  <Form name="endYear">
                    <Select name="endYear" label="Ending year" onChange={(v) => setEndyear(v)} initialValue={endYear} style={{ textAlign: "left" }}>
                      {years.map((year, i) => (
                        <Option key={i} value={year}>
                          {year}
                        </Option>
                      ))}
                    </Select>
                  </Form>
                </Col>
                <Col sm={8}>
                  <Form name="startItem">
                    <InputNumber
                      name="startItem"
                      initialValue={startItem}
                      label="Start item"
                      rules={[{ required: true }]}
                      min={0}
                      step={1}
                      onBlur={(v) => setStartItem(v.target.value)}
                    />
                  </Form>
                </Col>
              </Row>
              <Row>
                <Col sm={8}>
                  <Checkbox checked={easyExport} onChange={(e) => setEasyExport(e.target.checked)}>
                    Easy export
                  </Checkbox>
                </Col>
                <Col sm={8}>
                  <Checkbox checked={ensemble} onChange={(e) => setEnsemble(e.target.checked)}>
                    Ensemble model
                  </Checkbox>
                </Col>
                <Col sm={8}>
                  <Checkbox checked={groups} onChange={(e) => setGroups(e.target.checked)}>
                    Use groups
                  </Checkbox>
                </Col>
              </Row>
              <Row>
                <Col sm={24}>
                  <Upload name="file" showUploadList={false} action="" beforeUpload={(v) => fileUpload(v)} className="auto-height">
                    <Button type="primary">Upload zip & start</Button>
                  </Upload>
                </Col>
              </Row>
            </>
          )
        }
      />
      {predictions.length && geometries.length ? (
        <Row>
          {/* prelint-ignore-col */}
          {easyExport && (
            <Col sm={10}>
              <AdminTable
                className="no-action-table"
                dataSource={predictions.map((field, i) => {
                  if (field.field && field.data) {
                    return {
                      field: i,
                      [`${Object.keys(geometries[0].field)[0]}`]: `${Object.values(field.field)[0]}`,
                      year: field.year,
                    };
                  }
                  return {
                    field: i,
                    [`${Object.keys(geometries[0].field)[0]}`]: "...",
                    year: "",
                  };
                })}
                config={{
                  exclude: ["field"],
                }}
              />
            </Col>
          )}
          <Col sm={easyExport ? 14 : 24}>
            <AdminTable
              className="no-action-table"
              dataSource={predictions.map((field, i) => {
                if (field.field && field.data) {
                  const data = ensemble ? field.data.ensemble : field.data.crops;
                  const predictions = groups ? data.groups : data.predictions;

                  const optionalCols = easyExport
                    ? {}
                    : {
                        field: i,
                        year: field.year,
                        [`${Object.keys(geometries[0].field)[0]}`]: `${Object.values(field.field)[0]}`,
                        [`${Object.keys(geometries[0].field)[1]}`]: `${Object.values(field.field)[1]}`,
                        [`${Object.keys(geometries[0].field)[2]}`]: `${Object.values(field.field)[2]}`,
                        [`${Object.keys(geometries[0].field)[3]}`]: `${Object.values(field.field)[3]}`,
                        "Shapefile Crop": field.field.shapeCrop,
                      };

                  return {
                    ...optionalCols,
                    ...{
                      "Prediction 1": `${Object.keys(predictions)[0]} (${(Object.values(predictions)[0] * 100).toFixed(2)}%)`,
                      "Prediction 2": `${Object.keys(predictions)[1]} (${(Object.values(predictions)[1] * 100).toFixed(2)}%)`,
                      "Prediction 3": `${Object.keys(predictions)[2]} (${(Object.values(predictions)[2] * 100).toFixed(2)}%)`,
                    },
                  };
                }

                const loadingOptionalCols = easyExport
                  ? {}
                  : {
                      field: 0,
                      year: "",
                      [`${Object.keys(geometries[0].field)[0]}`]: `Retrieving ${i + parseInt(startItem) + 1}/${geometries.length * (totalYears + 1)}...`,
                      [`${Object.keys(geometries[0].field)[1]}`]: "",
                      [`${Object.keys(geometries[0].field)[2]}`]: "",
                      [`${Object.keys(geometries[0].field)[3]}`]: "",
                      "Shapefile Crop": "",
                    };

                return {
                  ...loadingOptionalCols,
                  ...{
                    "Prediction 1": `Retrieving ${i + parseInt(startItem) + 1}/${geometries.length * (totalYears + 1)}...`,
                    "Prediction 2": "",
                    "Prediction 3": "",
                  },
                };
              })}
              config={{
                exclude: ["field"],
                actions: easyExport
                  ? null
                  : [
                      {
                        icon: "tabler/eye",
                        func: openPredictions,
                        tooltip: "View full predictions",
                      },
                      {
                        icon: "tabler/chart-line",
                        func: openHeatmap,
                        tooltip: "View charts and data",
                      },
                    ],
              }}
            />
          </Col>
        </Row>
      ) : (
        <></>
      )}

      {details && details.data.crops.ndvi ? (
        <Drawer title="Field data" visible={showDetailsDrawer} onClose={() => setShowDetailsDrawer(false)}>
          <AdminSection headline="Scores collected" />
          <Row>
            <Col>This line chart has been removed, talk to Engineering if that's a problem</Col>
          </Row>
          {location && heatmap && heatmap.length ? (
            <>
              <AdminSection headline={`Normalised NDVI heatmap for ${heatmap[0].date}`} />
              <Row>
                <Col>
                  <Card className="map-card">
                    <MapInteractive
                      defaultZoom={16}
                      geometries={heatmap.map((geometry) => ({
                        ...geometry,
                        type: "shapeFile",
                        fillOpacity: getFillOpacity(geometry),
                        strokeOpacity: 0.1,
                        strokeWeight: 0.15,
                      }))}
                      location={location}
                      scaleControl={true}
                      zoomControl={true}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Row>
              <Col>
                <Spin />
              </Col>
            </Row>
          )}
        </Drawer>
      ) : (
        <></>
      )}

      {predictionDetails ? (
        <Drawer title="Prediction data" visible={showPredictionDetailsDrawer} onClose={() => setShowPredictionDetailsDrawer(false)}>
          <Row>
            <Col>
              <AdminTable dataSource={predictionDetails} />
            </Col>
          </Row>
        </Drawer>
      ) : (
        <></>
      )}
    </MainPage>
  );
};
